import React from 'react';
import { Badge } from '../Badge';
import { ChevronUpIcon, ChevronDownIcon, XIcon, PaperclipIcon, MailIcon, MessageSquareIcon, SendIcon, CheckCircleIcon, AlertTriangleIcon, ShieldIcon, CheckIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { getTypeBadgeVariant, getStatusBadgeVariant, type ReviewDocument, type Comment, type Classification, type DocReviewState, type FamilyMember, type CaseIssue } from './types';
interface DocumentDrawerProps {
  selectedDoc: ReviewDocument;
  drawerTab: 'summary' | 'family' | 'comments';
  selectedIndex: number;
  visibleFlatDocs: ReviewDocument[];
  docState: DocReviewState;
  newComment: string;
  getDocComments: (docId: string) => Comment[];
  getDocFamily: (doc: ReviewDocument) => FamilyMember[];
  getThreadSize: (threadId: string) => number;
  setDrawerTab: (tab: 'summary' | 'family' | 'comments') => void;
  navigatePrev: () => void;
  navigateNext: () => void;
  onClose: () => void;
  onReviewAndNext: () => void;
  onUnreview: () => void;
  onClassify: (c: Classification) => void;
  onTogglePrivileged: () => void;
  pendingTbd: boolean;
  caseIssues: CaseIssue[];
  docIssues: string[];
  onToggleIssue: (issueId: string) => void;
  handlePostComment: () => void;
  setNewComment: (v: string) => void;
  commentsEndRef: React.RefObject<HTMLDivElement | null>;
  commentInputRef: React.RefObject<HTMLTextAreaElement | null>;
}
export function DocumentDrawer({
  selectedDoc,
  drawerTab,
  selectedIndex,
  visibleFlatDocs,
  docState,
  newComment,
  getDocComments,
  getDocFamily,
  getThreadSize,
  setDrawerTab,
  navigatePrev,
  navigateNext,
  onClose,
  onReviewAndNext,
  onUnreview,
  onClassify,
  onTogglePrivileged,
  pendingTbd,
  caseIssues,
  docIssues,
  onToggleIssue,
  handlePostComment,
  setNewComment,
  commentsEndRef,
  commentInputRef
}: DocumentDrawerProps) {
  return <div className="absolute top-0 right-0 bottom-0 w-[480px] bg-navy-light border-l border-navy-lighter flex flex-col shadow-2xl" data-id="element-96" data-mp="4u9cxbrv5xt3">
      {/* Drawer Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-lighter shrink-0" data-id="element-97" data-mp="2wynm4o6gl2ye">
        <div className="flex items-center gap-2 min-w-0" data-id="element-98" data-mp="2iz4ia7kokdrw">
          <span className="font-mono text-xs text-gray-400" data-id="element-99" data-mp="2nyqwaj8c1y1r">
            {selectedDoc.id}
          </span>
          <span className="text-gray-600" data-id="element-100" data-mp="299kx2v9hw7tv">·</span>
          <Badge variant={getTypeBadgeVariant(selectedDoc.type)} data-id="element-101" data-mp="298teqm4ws6lj">
            {selectedDoc.type}
          </Badge>
        </div>
        <div className="flex items-center gap-1 shrink-0" data-id="element-102" data-mp="209eqltc7s847">
          <button onClick={() => window.open(`/documents/${selectedDoc.id}`, '_blank')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-amber hover:bg-navy border border-navy-lighter rounded-lg transition-colors mr-2" title="Open document in new tab" data-id="element-103" data-mp="20azkdgqpfn10">
            <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-104" data-mp="1bnrtlz2k6r2o" />
            Open
          </button>
          <span className="text-xs text-gray-500 mr-2" data-id="element-105" data-mp="17nga4plxmwss">
            {selectedIndex + 1 > 0 ? selectedIndex + 1 : '–'} of{' '}
            {visibleFlatDocs.length}
          </span>
          <button onClick={navigatePrev} disabled={selectedIndex <= 0} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-106" data-mp="1gnmk41if9en7">
            <ChevronUpIcon className="w-4 h-4" data-id="element-107" data-mp="1qlasvvcv4mod" />
          </button>
          <button onClick={navigateNext} disabled={selectedIndex >= visibleFlatDocs.length - 1} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-108" data-mp="24dqqggmzvshw">
            <ChevronDownIcon className="w-4 h-4" data-id="element-109" data-mp="29ck203iq19ff" />
          </button>
          <Tooltip content="Close" shortcut="Esc" position="bottom" align="end" data-id="element-110" data-mp="29a7bv32t7sra">
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors ml-1" data-id="element-111" data-mp="6p3lxguq5qi9">
              <XIcon className="w-4 h-4" data-id="element-112" data-mp="gghh5cfn845l" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-navy-lighter shrink-0" data-id="element-113" data-mp="ldrf5gh3lo51">
        <button onClick={() => setDrawerTab('summary')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${drawerTab === 'summary' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-114" data-mp="3jx83j1vj8ttg">
          Summary
          {drawerTab === 'summary' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-115" data-mp="2p2v0wqfvmub" />}
        </button>
        <button onClick={() => setDrawerTab('family')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative flex items-center justify-center gap-1.5 ${drawerTab === 'family' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-116" data-mp="193bxpy9cajds">
          <PaperclipIcon className="w-3 h-3" data-id="element-117" data-mp="2tlwbxsuql6s1" />
          Family
          {drawerTab === 'family' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-118" data-mp="2oue90z95kw4f" />}
        </button>
        <button onClick={() => setDrawerTab('comments')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative flex items-center justify-center gap-1.5 ${drawerTab === 'comments' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-119" data-mp="2jvjqlyf3l9ku">
          <MessageSquareIcon className="w-3 h-3" data-id="element-120" data-mp="25xms2wh32t3j" />
          Comments
          {getDocComments(selectedDoc.id).length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${drawerTab === 'comments' ? 'bg-amber/20 text-amber' : 'bg-navy-lighter text-gray-400'}`} data-id="element-121" data-mp="20zbhukmvmgks">
              {getDocComments(selectedDoc.id).length}
            </span>}
          {drawerTab === 'comments' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-122" data-mp="1tk9bcivegkcv" />}
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 flex flex-col" data-id="element-123" data-mp="1joxunv0fl32h">
        {drawerTab === 'summary' ? <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-124" data-mp="1ompxvl6j0blz">
            {/* AI Summary */}
            <div data-id="element-125" data-mp="2ccais9m8pixw">
              <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold" data-id="element-126" data-mp="2cdvdq0cgv3wa">
                AI Summary
              </div>
              <p className="text-sm text-gray-300 leading-relaxed" data-id="element-127" data-mp="2h9kf8981quv8">
                {selectedDoc.fullSummary || selectedDoc.summary}
              </p>
            </div>

            {/* Key Metadata Grid */}
            <div className="bg-navy rounded-lg border border-navy-lighter p-3 space-y-3" data-id="element-128" data-mp="27fjz7cb8857q">
              <div data-id="element-129" data-mp="r1two9zr9p07">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-130" data-mp="vybyp23pb98u">
                  Bates #
                </div>
                <div className="font-mono text-xs text-white" data-id="element-131" data-mp="3a51q5fjizndb">
                  {selectedDoc.id}
                </div>
              </div>

              <div className="pt-2 border-t border-navy-lighter space-y-2" data-id="element-132" data-mp="359mdgqx97xih">
                <div data-id="element-133" data-mp="3j689rxhh1eas">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-134" data-mp="3j4osq4nzqz7y">
                    From
                  </div>
                  <div className="text-xs text-gray-300" data-id="element-135" data-mp="2rgws8opjuwmf">
                    {selectedDoc.sender}
                  </div>
                </div>
                <div data-id="element-136" data-mp="2menxpnx13mi7">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-137" data-mp="3085i1axjh39a">
                    To
                  </div>
                  <div className="text-xs text-gray-300" data-id="element-138" data-mp="2vgndkcwb4kjk">
                    {selectedDoc.type === 'Email' ? 'legal-team@acme.com, review-group@acme.com' : '—'}
                  </div>
                </div>
                <div data-id="element-139" data-mp="2sl1t6hb05j7t">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-140" data-mp="2sll95sh3dxj7">
                    Subject
                  </div>
                  <div className="text-xs text-white font-medium" data-id="element-141" data-mp="2xkeolqfxvym0">
                    {selectedDoc.subject}
                  </div>
                </div>
              </div>

              {selectedDoc.threadId && <div className="pt-2 border-t border-navy-lighter flex items-center justify-between" data-id="element-142" data-mp="36igmhx4dmb52">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-143" data-mp="3bi392x63h3qj">
                    Thread
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-300" data-id="element-144" data-mp="3l9h68yakprgi">
                    <MailIcon className="w-3 h-3 text-gray-500" data-id="element-145" data-mp="3pbu9ng1ulda4" />
                    {getThreadSize(selectedDoc.threadId)} emails
                    {selectedDoc.isTerminal && <Badge variant="amber" className="text-[10px] px-1 py-0 ml-1" data-id="element-146" data-mp="34463bkpgi3j">
                        Terminal
                      </Badge>}
                  </div>
                </div>}
            </div>

            {/* Audit trail */}
            <div className="text-[10px] text-gray-500 text-center pt-1" data-id="element-147" data-mp="3amxphgbs2eq">
              Last accessed by Sarah Chen · Nov 20, 2023 at 14:32 EST
            </div>
          </div> : drawerTab === 'family' ? <div className="flex-1 overflow-y-auto p-4" data-id="element-148" data-mp="vxr5tvapswuq">
            <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-semibold" data-id="element-149" data-mp="qyxti675zbw5">
              Document Family
            </div>
            <div className="space-y-1" data-id="element-150" data-mp="m6wjkqxgxx9a">
              {getDocFamily(selectedDoc).map((member, idx) => <div key={member.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${member.id === selectedDoc.id ? 'bg-amber/5 border border-amber/20' : 'hover:bg-navy-hover border border-transparent'} ${member.type === 'attachment' ? 'ml-4' : ''}`} data-id="element-151" data-mp="i51j1ond3nwk" data-mpi={idx}>
                  <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${member.type === 'parent' ? 'bg-blue-500/10' : 'bg-navy-lighter'}`} data-id="element-152" data-mp="9dekzdgh6jdd">
                    {member.type === 'parent' ? <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-153" data-mp="ici3af7tiuqt" /> : <PaperclipIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-154" data-mp="x9950v4ysijl" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-155" data-mp="sa67rwv87gm6">
                    <div className="text-xs text-white truncate" data-id="element-156" data-mp="162cgixw1ulbl">
                      {member.name}
                    </div>
                    <div className="text-[10px] text-gray-500 flex items-center gap-2" data-id="element-157" data-mp="122loyryp8160">
                      {member.type === 'parent' ? <span data-id="element-158" data-mp="1b30fc5qis6gd">Parent email</span> : <>
                          {member.fileType && <span data-id="element-159" data-mp="1upbmm1htz7o6">{member.fileType}</span>}
                          {member.size && <span data-id="element-160" data-mp="1znwrwfbxwhjh">· {member.size}</span>}
                        </>}
                      {member.id === selectedDoc.id && <span className="text-amber" data-id="element-161" data-mp="1ks7020k2pa6u">← Current</span>}
                    </div>
                  </div>
                </div>)}
            </div>

            {getDocFamily(selectedDoc).length <= 1 && <div className="text-center py-8" data-id="element-162" data-mp="1gpkgso1gwxl9">
                <PaperclipIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" data-id="element-163" data-mp="1gqc1h3tj1awr" />
                <p className="text-xs text-gray-500" data-id="element-164" data-mp="121fwjfmroe0d">
                  No attachments or related documents
                </p>
              </div>}
          </div> : <>
            {/* Comments Tab */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-165" data-mp="x0a1hb5mweno">
              {getDocComments(selectedDoc.id).length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-166" data-mp="2zo8kteepnc29">
                  <MessageSquareIcon className="w-8 h-8 text-gray-600 mb-2" data-id="element-167" data-mp="2uvfhmuyw4bsa" />
                  <p className="text-xs text-gray-400" data-id="element-168" data-mp="2px452ch7nn6d">No comments yet</p>
                  <p className="text-[10px] text-gray-500 mt-1" data-id="element-169" data-mp="2pv1632y2yjpi">
                    Be the first to leave a note.
                  </p>
                </div> : getDocComments(selectedDoc.id).map(comment => <div key={comment.id} className="flex gap-2.5" data-id="element-170" data-mp="3dnzvp69xkcc1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${comment.isCurrentUser ? 'bg-amber/20 text-amber' : 'bg-navy-lighter text-gray-300'}`} data-id="element-171" data-mp="38nvflhcxpjmc">
                      {comment.initials}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-172" data-mp="1t6v6wvjxgjim">
                      <div className="flex items-baseline gap-2 mb-0.5" data-id="element-173" data-mp="1t9hfmyik152f">
                        <span className={`text-xs font-medium ${comment.isCurrentUser ? 'text-amber' : 'text-white'}`} data-id="element-174" data-mp="8qng3tn24yot">
                          {comment.author}
                        </span>
                        <span className="text-[10px] text-gray-500" data-id="element-175" data-mp="mg7bgeeb6to3">
                          {comment.timestamp}
                        </span>
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed" data-id="element-176" data-mp="rho9m25es20z">
                        {comment.text}
                      </p>
                    </div>
                  </div>)}
              <div ref={commentsEndRef} data-id="element-177" data-mp="15fctb6nqp34g" />
            </div>

            {/* Comment Input */}
            <div className="p-3 border-t border-navy-lighter shrink-0" data-id="element-178" data-mp="327mtkcdn36t2">
              <div className="flex gap-2" data-id="element-179" data-mp="329g0lss5zt1l">
                <textarea ref={commentInputRef} value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handlePostComment();
              }
            }} placeholder="Add a comment..." rows={2} className="flex-1 bg-navy border border-navy-lighter rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors resize-none" data-id="element-180" data-mp="3766fwdw5akly" />
                <button onClick={handlePostComment} disabled={!newComment.trim()} className="self-end p-2 bg-amber hover:bg-amber-hover text-navy rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Post comment (Enter)" data-id="element-181" data-mp="22wwo1vh5flhp">
                  <SendIcon className="w-3.5 h-3.5" data-id="element-182" data-mp="15ed1ookuat4r" />
                </button>
              </div>
            </div>
          </>}
      </div>

      {/* Review & Classification Footer */}
      <div className="px-4 py-4 border-t border-navy-lighter bg-[#0A1520] shrink-0 space-y-4" data-id="element-183" data-mp="3lvubtroufavy">
        {/* Classification */}
        <div data-id="element-184" data-mp="3c4pwgqv99o0l">
          <div className="flex gap-2" data-id="element-185" data-mp="3c46mpxfre5xn">
            <button onClick={() => onClassify('hot')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${docState.classification === 'hot' ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-186" data-mp="3c59c20xfo0bg">
              <FlameIcon className="w-3.5 h-3.5" data-id="element-187" data-mp="3utwjur6zv3sq" />
              Hot
            </button>
            <button onClick={() => onClassify('critical')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${docState.classification === 'critical' ? 'bg-orange-500/15 border-orange-500/40 text-orange-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-188" data-mp="2fde8l5cdynyi">
              <AlertOctagonIcon className="w-3.5 h-3.5" data-id="element-189" data-mp="g5xpfmk8b3nv" />
              Critical
            </button>
            <button onClick={() => onClassify('tbd')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${pendingTbd ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300 animate-pulse' : docState.classification === 'tbd' ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-190" data-mp="b7w6d1msz4ce">
              <HelpCircleIcon className="w-3.5 h-3.5" data-id="element-191" data-mp="1rle12qh0d5fl" />
              {pendingTbd ? 'TBD — awaiting comment' : 'TBD'}
            </button>
          </div>
          {pendingTbd && <div className="mt-2 text-[10px] text-yellow-400 flex items-center gap-1" data-id="element-192" data-mp="1v5efxcmnh281">
              <AlertTriangleIcon className="w-3 h-3" data-id="element-193" data-mp="1z5ojkimyzgjq" />
              Post a comment to confirm TBD classification
            </div>}
        </div>

        {/* Issues */}
        <div className="pt-3 border-t border-navy-lighter" data-id="element-194" data-mp="28vzzz4nubi7i">
          <div className="flex flex-wrap gap-1.5" data-id="element-195" data-mp="28vhyntmbmdsq">
            {caseIssues.map(issue => {
            const isTagged = docIssues.includes(issue.id);
            return <button key={issue.id} onClick={() => onToggleIssue(issue.id)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all duration-200 border ${isTagged ? 'border-transparent' : 'border-navy-lighter text-gray-500 hover:text-gray-300 hover:border-gray-600 bg-navy'}`} style={isTagged ? {
              backgroundColor: issue.color + '20',
              color: issue.color,
              borderColor: issue.color + '40'
            } : {}} data-id="element-196" data-mp="1g8dq5eo0rhc7">
                  <div className="w-2 h-2 rounded-full shrink-0 transition-colors" style={{
                backgroundColor: isTagged ? issue.color : '#4b5563'
              }} data-id="element-197" data-mp="aptexgxmk1j" />
                  {issue.label}
                </button>;
          })}
          </div>
        </div>

        {/* Privileged Toggle */}
        <div className={`pt-3 border-t flex items-center justify-between ${docState.privileged ? 'border-red-500/30' : 'border-navy-lighter'}`} data-id="element-198" data-mp="j7h80tlj4az9">
          <div className="flex items-center gap-2" data-id="element-199" data-mp="2kyki9hribnja">
            <ShieldIcon className={`w-4 h-4 ${docState.privileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-200" data-mp="2l1gi5bmx0mad" />
            <div data-id="element-201" data-mp="vii4uohw9za8">
              <div className={`text-xs font-medium ${docState.privileged ? 'text-red-400' : 'text-gray-400'}`} data-id="element-202" data-mp="vhyrzm73q132">
                Privileged
              </div>
              {docState.privileged && docState.privilegedBy && <div className="text-[9px] text-red-400/60" data-id="element-203" data-mp="mo3sousnv4vc">
                  Set by {docState.privilegedBy}
                </div>}
            </div>
          </div>
          <button onClick={onTogglePrivileged} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${docState.privileged ? 'bg-red-500' : 'bg-navy-lighter'}`} data-id="element-204" data-mp="cqfntc1ccgzg">
            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${docState.privileged ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-205" data-mp="7snymps2z8yy" />
          </button>
        </div>

        {/* Review & Next — at the very bottom */}
        <div className="pt-3 border-t border-navy-lighter flex items-center justify-between" data-id="element-206" data-mp="224kr8m29ekvl">
          <div className="flex items-center gap-2" data-id="element-207" data-mp="mbhnfr1r3bna">
            {docState.reviewed ? <div className="flex items-center gap-1.5 text-green-400 text-xs font-medium" data-id="element-208" data-mp="r5lvyeqnzrqh">
                <CheckCircleIcon className="w-4 h-4" data-id="element-209" data-mp="cgy4oknuq46c" />
                Reviewed by {docState.reviewedBy}
              </div> : <div className="text-gray-500 text-xs" data-id="element-210" data-mp="1542ausyokojp">Not reviewed</div>}
          </div>
          <div className="flex items-center gap-2" data-id="element-211" data-mp="1a35afxvw62ka">
            {docState.reviewed && <button onClick={onUnreview} className="flex items-center gap-2 px-3 py-2 bg-navy border border-navy-lighter hover:border-red-500/40 hover:bg-red-500/10 text-gray-400 hover:text-red-400 font-medium rounded-lg transition-colors text-xs" data-id="element-212" data-mp="v5wd8njwq4kn">
                Unreview
              </button>}
            <Tooltip content={docState.reviewed ? 'Already reviewed' : 'Review & Next'} shortcut={docState.reviewed ? undefined : 'R'} data-id="element-213" data-mp="3kwrkexaczbmc">
              <button onClick={onReviewAndNext} disabled={docState.reviewed} className={`flex items-center gap-2 px-4 py-2 font-semibold rounded-lg transition-colors text-sm ${docState.reviewed ? 'bg-green-500/15 text-green-400 border border-green-500/30 cursor-default' : 'bg-amber hover:bg-amber-hover text-navy shadow-lg shadow-amber/20'}`} data-id="element-214" data-mp="1xxfauxdufhs0">
                <CheckIcon className="w-4 h-4" data-id="element-215" data-mp="1f9ack5fygp0l" />
                {docState.reviewed ? 'Reviewed' : 'Reviewed & Next'}
                {!docState.reviewed && <span className="text-navy/50 text-xs font-medium ml-1" data-id="element-216" data-mp="1o8xgaxw5q6wt">
                    (R)
                  </span>}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>;
}